import * as ents from '@attain-apps/entities'
import { TypeChecker } from '@attain-apps/type-guards'

/**
 * {@link https://ngrx.io/docs State} of this feature
 */
export interface State {
  error?: Error
  /**
   * Functional usage to apply to every selected record. `undefined` while no
   * bulk update is in progress.
   */
  update?: Partial<ents.extra.space.Record.FunctionalUsage.Update>
  isSubmitting: boolean
}

export namespace State {
  export const INITIAL: Readonly<State> = {
    error: undefined,
    isSubmitting: false,
    update: undefined,
  }

  /** Whether a bulk functional usage update is in progress */
  export function isUpdating(state: State): boolean {
    return TypeChecker.isNotNullish(state.update)
  }

  /**
   * Opens a bulk functional usage update for the current selection, on Manual.
   *
   */
  export function onInitiate(): State {
    return {
      ...INITIAL,
      update: {
        functionalizationMethodology:
          ents.extra.space.Record.FunctionalUsage.Methodology.Manual,
      },
    }
  }

  /**
   * Stores the methodology, dropping anything the previous one carried.
   *
   * Allocations and the service center only mean something for the
   * methodology they were picked under.
   */
  export function onSetMethodology(
    functionalizationMethodology: ents.extra.space.Record.FunctionalUsage.Methodology,
    state: State,
  ): State {
    return isUpdating(state)
      ? { ...state, update: { functionalizationMethodology } }
      : state
  }

  /** Stores the service center whose allocations will be applied */
  export function onSetServiceCenter(
    serviceCenterId: ents.extra.space.ServiceCenter.Id | undefined,
    state: State,
  ): State {
    return isUpdating(state)
      ? { ...state, update: { ...state.update, serviceCenterId } }
      : state
  }

  /** Updates one allocation's percentage, adding it when it isn't there yet */
  export function onUpdateFunctionalization(
    { functionId, percent }: ents.extra.space.Functionalization,
    state: State,
  ): State {
    if (!isUpdating(state)) {
      return state
    }

    const existing = state.update?.functionalizations ?? []
    const index = existing.findIndex(
      functionalization => functionalization.functionId === functionId,
    )

    // If found, replace it in place; otherwise append it to the list
    const functionalizations = [...existing]
    if (index >= 0) {
      functionalizations.splice(index, 1, { functionId, percent })
    } else {
      functionalizations.push({ functionId, percent })
    }

    return { ...state, update: { ...state.update, functionalizations } }
  }

  /** Marks a bulk functional usage request as in flight */
  export function onRequest(state: State): State {
    return { ...state, error: undefined, isSubmitting: true }
  }

  /** Records a failed bulk functional usage request */
  export function onError(error: Error, state: State): State {
    return { ...state, error, isSubmitting: false }
  }

  /** Clears the in flight marker for a request that never ran */
  export function onCancellation(state: State): State {
    return { ...state, isSubmitting: false }
  }
}
